import { Pagination, PickingArea } from '@ekkogmbh/apisdk';
import { Add, Check, Clear, Delete, Edit } from '@mui/icons-material';
import React, { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import { DataTable } from '../../Common/Components/DataTable';

import Permissions from 'src/Common/Permissions';
import { PickingAreaPanel } from './PickingAreaPanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import EnabledNameField from 'src/Common/Components/DataTable/EnabledNameField';
import { Stack } from '@mui/material';
import { BlinkColorItem } from 'src/Common/Components/BlinkColorItem';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';

export const PickingAreasContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();
  const [selectedArea, setSelectedArea] = useState<PickingArea>();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);

  const [, startRequest] = useRequest();
  const hasWritePermission = useMemo(() => hasPermission(Permissions.PICKING_AREAS_WRITE), [hasPermission]);

  const dismiss = useCallback(() => {
    setDialogOpen(false);
    setPanelOpen(false);
    setSelectedArea(undefined);
  }, []);

  const fetchAreas = useCallback((pagination: Pagination) => api.getPickingAreas(pagination), [api]);

  const deleteArea = useCallback(() => {
    if (selectedArea === undefined) {
      return;
    }

    startRequest(
      async () => await api.deletePickingArea(selectedArea),
      () => {
        refresh();
        dismiss();
      },
    );
  }, [api, selectedArea]);

  return (
    <ContentControl
      panel={<PickingAreaPanel pickingArea={selectedArea} closeHandler={dismiss} />}
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          onClick={() => {
            dismiss();
            setSelectedArea(undefined);
            setPanelOpen(true);
          }}
          tooltip={'Add Picking-Area'}
          requiredPermission={Permissions.PICKING_AREAS_WRITE}
        />,
      ]}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Picking-Area'}
          text={
            <>
              <div>
                Delete Picking-Area: <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedArea?.id ?? ''}</span>
              </div>
              <div>
                with Required Side:{' '}
                <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedArea?.requiredSide.name ?? ''}</span>?
              </div>
            </>
          }
          onClose={dismiss}
          onConfirm={deleteArea}
        />
      }
    >
      <DataTable
        fetchItems={fetchAreas}
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: PickingArea) => {
              dismiss();
              setSelectedArea(entry);
              setPanelOpen(true);
            },
            disabled: !hasWritePermission,
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: PickingArea) => {
              dismiss();
              setSelectedArea(entry);
              setDialogOpen(true);
            },
            disabled: !hasWritePermission,
          },
        ]}
        columns={[
          {
            header: 'External-Identifier',
            body: (entry: PickingArea) => entry.id,
            filterField: 'id',
            sortField: 'PA.externalIdentifier',
          },
          {
            header: 'Picking-Type',
            body: (entry: PickingArea) => entry.type,
            filterField: 'type',
            sortField: 'PA.type',
          },
          {
            header: 'Required-Side',
            body: (entry: PickingArea) => (
              <EnabledNameField
                name={entry.requiredSide.name}
                enabled={entry.requiredSideAck}
                iconTooltip={entry.requiredSideAck ? 'ACK' : 'NO-ACK'}
              />
            ),
            filterField: 'requiredSide',
            sortField: 'RS.name',
          },
          {
            header: 'Optional-Side',
            body: (entry: PickingArea) => {
              const { optionalSide, optionalSideAck, symmetricalAck } = entry;
              if (optionalSide == null) {
                return '-';
              }

              return (
                <EnabledNameField
                  name={entry.optionalSide!.name}
                  enabled={optionalSideAck ? true : symmetricalAck ? undefined : false}
                  iconTooltip={optionalSideAck ? 'ACK' : symmetricalAck ? 'SYMMETRIC-ACK' : 'NO-ACK'}
                />
              );
            },
            filterField: 'optionalSide',
            sortField: 'OS.name',
          },
          {
            header: 'Blink-Colors',
            body: (entry: PickingArea) => (
              <Stack direction={'column'}>
                {entry.colors.map((color, index) => (
                  <BlinkColorItem key={index} color={color} />
                ))}
              </Stack>
            ),
          },
          {
            header: 'Next-Color',
            body: (entry) => <BlinkColorItem color={entry.nextColor} />,
          },
          {
            header: 'Blink-Count',
            body: (entry: PickingArea) => entry.simultaneousBlinkCount,
          },
          {
            header: 'Blink-Duration',
            body: (entry: PickingArea) => entry.defaultBlinkDuration,
          },
          {
            header: 'Concurrent-Jobs',
            body: (entry: PickingArea) => entry.simultaneousBlinkCount,
          },
          {
            header: 'Acknowledge-With-Button',
            body: (entry: PickingArea) => {
              const { ackByButtonTopic } = entry;
              return (
                <TooltipHardened
                  tooltip={
                    ackByButtonTopic ? (
                      <span>
                        topic: <b>{ackByButtonTopic}</b>
                      </span>
                    ) : undefined
                  }
                  placement={'top'}
                >
                  {ackByButtonTopic ? <Check /> : <Clear />}
                </TooltipHardened>
              );
            },
          },
          {
            header: 'Automatic-Job-Finish',
            body: (entry: PickingArea) => (entry.automaticJobFinish ? <Check /> : <Clear />),
          },
        ]}
      />
    </ContentControl>
  );
};
